/**
 * One-click motion setups seeded from the Photo Studio reel platforms, so a
 * timeline export and a Photo Studio reel for the same destination match in
 * size and length.
 */

import type { VideoProject } from '@canvas/shared-types';
import { REEL_PLATFORMS } from '@/data/reelPlatforms';
import { applySizePreset } from '@/data/videoExportPresets';
import { setSceneDuration } from '@/lib/video/sceneTimeline';

export interface MotionTemplate {
  id: string;
  label: string;
  width: number;
  height: number;
  sceneDurationMs: number;
  hint: string;
}

export const MOTION_TEMPLATES: MotionTemplate[] = REEL_PLATFORMS.map((p) => ({
  id: p.id,
  label: p.label,
  width: p.width,
  height: p.height,
  sceneDurationMs: p.defaultDurationSec * 1000,
  hint: p.tip,
}));

export function motionTemplateById(id: string): MotionTemplate | null {
  return MOTION_TEMPLATES.find((t) => t.id === id) ?? null;
}

/**
 * Point the export at the platform and give every scene its default length.
 * Scene durations still stop at the 5-minute timeline cap.
 */
export function applyMotionTemplate(video: VideoProject, template: MotionTemplate): VideoProject {
  let next: VideoProject = {
    ...video,
    exportSettings: applySizePreset(video.exportSettings, template.id, {
      width: template.width,
      height: template.height,
    }),
  };
  for (const scene of video.scenes) {
    next = setSceneDuration(next, scene.pageId, template.sceneDurationMs);
  }
  return next;
}
